import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import type { QuestScope } from "@-/dev.improve.haniverse.lib.quest.catalog";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { completeQuest, unlockQuest } from "#quest-writes";

function useGetUserQuestsQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(["user_quest"]));
  return () => withTableChangeVersion(["userQuests", { actorDb }] as const);
}

/** Every quest the learner has unlocked, completed or not. */
export const [useUserQuestsQueryOptions] = createUseQueryOptions(
  useGetUserQuestsQueryKey,
  async ({ queryKey: [_, { actorDb }] }) =>
    actorDb(import.meta.location).query.userQuest.findMany({}),
);

/** Mutations for playing a quest; each reads `now` when it runs. */
export function useQuestActions({ isPremium }: { isPremium: boolean }) {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const db = () => actorDb(import.meta.location);
  const unlockMutation = useMutation({
    mutationFn: (input: { costHan: number; questId: string; scope: QuestScope }) =>
      unlockQuest({ actorDb: db(), isPremium, now: new Date(), ...input }),
    mutationKey: ["quest", "unlock"],
  });
  const completeMutation = useMutation({
    mutationFn: (input: { questId: string; xpEarned: number }) =>
      completeQuest({ actorDb: db(), now: new Date(), ...input }),
    mutationKey: ["quest", "complete"],
  });
  return { completeMutation, unlockMutation };
}
